﻿import { useLocation } from 'react-router-dom';
import { useContext, useState } from 'react';
import { MemberContext, Permission } from '../contexts/MemberContext';
import { CircularProgress, Stack } from '@mui/material';
import { NotificationsButton, NotificationsDrawer } from './NotificationsDrawer';
import { ProfileButton } from './ProfileButton';
import { AdministrationMenuButton } from './AdminstrationMenuButton';
import { NotificationContext } from '../contexts/NotificationContext';
import { sitePaths } from '../routes/paths';
import { pageAssets } from '../assets/pages';

type NavigationTab = {
  label: string;
  route: string;
  href: string;
  loggedIn: boolean;
  permission: Permission | null;
};

export default function NavBar() {
  const location = useLocation();
  const { isLoggedIn, isLoading: isLoadingMember, hasPermission } = useContext(MemberContext);
  const { hasNew: hasNewNotifications, clearNewStatus: clearNewNotificationStatus } =
    useContext(NotificationContext);
  const [openNotifications, setOpenNotifications] = useState(false);
  const path = location.pathname;

  const toggleNotificationDrawer = () => {
    setOpenNotifications(!openNotifications);
    if (hasNewNotifications && !openNotifications) {
      clearNewNotificationStatus();
    }
  };

  // Function to determine if a nav link is active
  const isActive = (navPath: string) => {
    if (navPath === '/') {
      return path === '/' || path.startsWith('/pedigree');
    }
    return path.startsWith(navPath);
  };

  const tabs: NavigationTab[] = [
    { label: 'Pedigree', route: '/', href: '/', loggedIn: false, permission: null },
    {
      label: pageAssets.members.title,
      route: sitePaths.member.root,
      href: sitePaths.member.root,
      loggedIn: false,
      permission: null,
    },
    {
      label: pageAssets.calfReturn.title,
      route: sitePaths.calf.return.root,
      href: sitePaths.calf.return.root,
      loggedIn: true,
      permission: Permission.CalfReturnView,
    },
    {
      label: pageAssets.activeDamList.navTitle,
      route: sitePaths.adl.root,
      href: sitePaths.adl.root,
      loggedIn: true,
      permission: Permission.AdlView,
    },
    {
      label: pageAssets.herd.audit.title,
      route: sitePaths.herd.audit.root,
      href: sitePaths.herd.audit.root,
      loggedIn: true,
      permission: Permission.HerdAuditView,
    },
    {
      label: pageAssets.pedigree.transfers.title,
      route: sitePaths.transfer.root,
      href: sitePaths.transfer.root,
      loggedIn: true,
      permission: Permission.TransferView,
    },
    {
      label: pageAssets.pedigree.registrations.title,
      route: sitePaths.registration.root,
      href: sitePaths.registration.root,
      loggedIn: true,
      permission: Permission.RegistrationView,
    },
  ];

  return (
    <>
      <header>
        <nav className="navbar navbar-expand-lg navbar-light bg-light sticky-top">
          <div className="container-fluid me-2">
            {/* Toggle button */}
            <button
              className="navbar-toggler"
              type="button"
              data-mdb-toggle="collapse"
              data-mdb-target="#navbarSupportedContent"
              aria-controls="navbarSupportedContent"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <i className="fas fa-bars"></i>
            </button>

            {/* Collapsible wrapper */}
            <div className="collapse navbar-collapse" id="navbarSupportedContent">
              <a className="navbar-brand" href="#">
                <img
                  src="/img/the-galloways.png"
                  className="me-3"
                  height={40}
                  alt="NZ Galloway Logo"
                  loading="lazy"
                />
                <Stack
                  direction="column"
                  spacing={0}
                  sx={{
                    fontFamily: 'Cardo',
                    color: '#3c5e4e',
                    fontWeight: 700,
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                  }}
                >
                  <div>Galloway Cattle Society</div>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <hr style={{ width: 50, margin: '0' }} />
                    <span>of</span>
                    <hr style={{ width: 50, margin: '0' }} />
                  </Stack>
                  <div>New Zealand</div>
                </Stack>
              </a>
              {/* Left links */}

              <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                {tabs
                  .filter(
                    (tab) =>
                      ((tab.loggedIn && isLoggedIn) || !tab.loggedIn) &&
                      (tab.permission === null || hasPermission(tab.permission))
                  )
                  .map((tab) => (
                    <li className="nav-item" key={tab.label}>
                      <a
                        className={`nav-link ${isActive(tab.route) ? 'active' : ''}`}
                        href={tab.href}
                      >
                        {tab.label}
                      </a>
                    </li>
                  ))}
              </ul>
              {/* Left links */}
            </div>

            {/* Right elements */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {isLoadingMember ? (
                <CircularProgress size={30} />
              ) : isLoggedIn ? (
                <>
                  <AdministrationMenuButton />
                  <NotificationsButton onClick={toggleNotificationDrawer} />
                  <ProfileButton />
                </>
              ) : (
                <a type="button" className="btn btn-link px-3 me-2" href="/auth/login">
                  Login
                </a>
              )}
            </Stack>
            {/* Right elements */}
          </div>
        </nav>
      </header>
      {!isLoadingMember && isLoggedIn && (
        <NotificationsDrawer open={openNotifications} onClose={toggleNotificationDrawer} />
      )}
    </>
  );
}
